// Si el botón «Cancelar venta» nace activo o deshabilitado, y el texto que lo explica.
import { formatDateMx } from '@/lib/dateHelpers';
import type {
    SaleCancellation,
    SaleCancellationReason,
} from '@/api/services/retail-wizard/contract';
import type { Translate } from './returnConditions';

/** Estado de la lectura del detalle, que es la única que trae `cancellation`. */
export type SaleDetailStatus = 'pending' | 'error' | 'success';

/** El backoffice cancela fuera de plazo con una excepción LATE_CANCEL; en el portal el plazo es final. */
export type CancelAvailabilityMode = 'backoffice' | 'portal';

export interface CancelAvailability {
    enabled: boolean;
    /** Texto bajo el botón; ausente cuando no hay nada que explicar. */
    note?: string;
    /** Excepción LATE_CANCEL aprobada que viaja al cancelar fuera de la ventana. */
    lateCancelExceptionId?: string;
}

/** Rechazos de la cancelación que significan que el detalle cargado ya no es vigente. */
export const STALE_SALE_CANCEL_CODES: ReadonlySet<string> = new Set([
    'CREDIT_HAS_PAYMENTS',
    'CREDIT_NOT_CANCELLABLE',
    'SALE_ALREADY_CANCELLED',
    'CANCEL_WINDOW_EXPIRED',
]);

const REASON_TEXT: Record<
    Exclude<SaleCancellationReason, 'CANCEL_WINDOW_EXPIRED'>,
    { key: string; text: string }
> = {
    SALE_ALREADY_CANCELLED: {
        key: 'cancelar.bloqueo.yaCancelada',
        text: 'No se puede cancelar: la venta ya está cancelada.',
    },
    CREDIT_HAS_PAYMENTS: {
        key: 'cancelar.bloqueo.creditoConPagos',
        text: 'No se puede cancelar: el crédito ya tiene pagos.',
    },
    CREDIT_NOT_CANCELLABLE: {
        key: 'cancelar.bloqueo.creditoNoCancelable',
        text: 'No se puede cancelar: el crédito ya no admite la cancelación.',
    },
    IMEI_NOT_RETURNABLE: {
        key: 'cancelar.bloqueo.equipoNoRetornable',
        text: 'No se puede cancelar: el equipo no se puede devolver al inventario.',
    },
};

/** Ventana vencida en el portal: no hay excepción posible. */
function portalWindowExpired(windowEndsAt: string | undefined, t: Translate): CancelAvailability {
    return {
        enabled: false,
        note: windowEndsAt
            ? t('cancelar.bloqueo.ventanaVencidaFechaPortal', {
                  defaultValue: 'Pasó el plazo para cancelar (vencía el {{fecha}}).',
                  fecha: formatDateMx(windowEndsAt),
              })
            : t('cancelar.bloqueo.ventanaVencidaPortal', {
                  defaultValue: 'Pasó el plazo para cancelar.',
              }),
    };
}

/** Ventana vencida en el backoffice: solo se cancela con una excepción LATE_CANCEL aprobada. */
function backofficeWindowExpired(
    windowEndsAt: string | undefined,
    lateCancelExceptionId: string | undefined,
    t: Translate,
): CancelAvailability {
    if (lateCancelExceptionId) {
        return {
            enabled: true,
            lateCancelExceptionId,
            note: t('cancelar.bloqueo.conExcepcion', {
                defaultValue: 'Pasó el plazo; se cancela con la excepción aprobada.',
            }),
        };
    }
    return {
        enabled: false,
        note: windowEndsAt
            ? t('cancelar.bloqueo.ventanaVencidaFecha', {
                  defaultValue:
                      'Pasó el plazo para cancelar (vencía el {{fecha}}). Necesitas una excepción de cancelación tardía aprobada en Excepciones.',
                  fecha: formatDateMx(windowEndsAt),
              })
            : t('cancelar.bloqueo.ventanaVencida', {
                  defaultValue:
                      'Pasó el plazo para cancelar. Necesitas una excepción de cancelación tardía aprobada en Excepciones.',
              }),
    };
}

/** Decide el botón con `cancellation` del detalle; sin ella (backend viejo) queda activo. */
export function cancelAvailability(
    cancellation: SaleCancellation | undefined,
    detailStatus: SaleDetailStatus,
    t: Translate,
    mode: CancelAvailabilityMode = 'backoffice',
): CancelAvailability {
    if (detailStatus === 'pending') {
        return {
            enabled: false,
            note: t('cancelar.bloqueo.revisando', {
                defaultValue: 'Revisando si esta venta se puede cancelar…',
            }),
        };
    }
    if (detailStatus === 'error') {
        return {
            enabled: false,
            note: t('cancelar.bloqueo.sinDetalle', {
                defaultValue:
                    'No pudimos revisar si esta venta se puede cancelar. Recarga la página e intenta de nuevo.',
            }),
        };
    }
    if (!cancellation || cancellation.allowed) return { enabled: true };
    if (cancellation.reason === 'CANCEL_WINDOW_EXPIRED') {
        return mode === 'portal'
            ? portalWindowExpired(cancellation.windowEndsAt, t)
            : backofficeWindowExpired(
                  cancellation.windowEndsAt,
                  cancellation.lateCancelExceptionId,
                  t,
              );
    }
    const mapped = cancellation.reason ? REASON_TEXT[cancellation.reason] : undefined;
    return {
        enabled: false,
        note: mapped
            ? t(mapped.key, { defaultValue: mapped.text })
            : t('cancelar.bloqueo.generico', {
                  defaultValue: 'No se puede cancelar esta venta.',
              }),
    };
}
